/**
 * Messages slice for the application store.
 * Handles chat messages and active conversation state.
 */

import { MessageRole } from '@/services/chat';
import { logger } from '@/utils/logger';
import { applyActivityUpdate } from '@features/chat/utils/activitySteps';

import type { Message, Conversation } from '../types';
import type { StreamEvent } from '@features/chat/html/StreamRenderer';
import type { ActivityStep } from '@features/chat/utils/activitySteps';
import type { StateCreator } from 'zustand';

/**
 * Messages state and actions.
 */
/**
 * A message written before its conversation exists, sent by `useChatSetup` once
 * the chat page picks it up. Written by `prepareConversation` — from the page's
 * own composer on the first message of a new chat, and from GoBox / askConway
 * outside the page.
 */
export interface PendingMessage {
  content: string;
  conversationId: string;
  streamingMessageId: string;
  /** Artifact ids for images uploaded before the conversation existed. */
  artifactIds?: string[] | undefined;
  clientMessageId?: string | undefined;
  /**
   * Per-message image-generation config (e.g. aspect_ratio, image_size) when
   * the chosen model is image-generation. Keys come from the model's
   * `configSchema`.
   */
  imageConfig?: Record<string, string | number> | undefined;
  /**
   * The model the composer had selected when the message was written. Preferred
   * over the `default_model_id` setting on the first send; absent for callers
   * outside the page (GoBox / askConway), which fall back to the setting.
   * `null` is an explicit choice of the server default.
   */
  modelId?: string | null | undefined;
  /** The prompt chosen in the composer, consumed with this message. */
  promptId?: string | undefined;
}

export interface MessagesSlice {
  // State
  activeConversation: Conversation | null;
  messages: Message[];
  isStreaming: boolean;
  isLoadingConversation: boolean;
  lastActiveConversationId: string | null;
  pendingMessage: PendingMessage | null;
  contextOverflowConversationIds: Set<string>;
  /** Conversation id currently being compacted, or null. Shared so every
   *  Compact affordance (button + bars) reflects one in-flight compaction. */
  compactingConversationId: string | null;

  // Actions
  setActiveConversation: (conversation: Conversation | null) => void;
  /**
   * Merge a partial patch into the active conversation in place WITHOUT
   * rebuilding the message list (unlike setActiveConversation). Keeps
   * header-visible fields like the title in sync after a rename/regenerate.
   * No-op when there is no active conversation.
   */
  patchActiveConversation: (patch: Partial<Conversation>) => void;
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  insertMessageBefore: (message: Message, beforeId: string) => void;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  appendToMessage: (id: string, content: string) => void;
  /**
   * Push a single html tokenizer event onto a message's transient
   * `htmlEvents` array (creating it if absent). A NEW array is created on
   * every push so React/Zustand consumers re-render. No-op if the message
   * is not found. Used only for live html turns; never persisted.
   */
  appendHtmlEvent: (id: string, event: StreamEvent) => void;
  /**
   * Apply a pure update to a message's transient tool `activity`. Functional,
   * so two tool events in one tick both land.
   */
  updateActivity: (id: string, fn: (steps: ActivityStep[]) => ActivityStep[]) => void;
  removeMessage: (id: string) => void;
  clearMessages: () => void;
  setIsStreaming: (streaming: boolean) => void;
  setIsLoadingConversation: (loading: boolean) => void;
  setPendingMessage: (pending: PendingMessage | null) => void;
  addContextOverflow: (conversationId: string) => void;
  clearContextOverflow: (conversationId: string) => void;
  setCompacting: (conversationId: string | null) => void;
}

/**
 * Creates the messages slice.
 */
export const createMessagesSlice: StateCreator<MessagesSlice, [], [], MessagesSlice> = (
  set,
  get
) => ({
  // Initial state
  activeConversation: null,
  messages: [],
  isStreaming: false,
  isLoadingConversation: false,
  lastActiveConversationId: null,
  pendingMessage: null,
  contextOverflowConversationIds: new Set<string>(),
  compactingConversationId: null,

  // Actions
  setActiveConversation: conversation => {
    logger.debug('🗂️ Store: setActiveConversation called with:', {
      hasConversation: !!conversation,
      conversationId: conversation?.id,
      originalMessageCount: conversation?.messages?.length || 0,
    });

    const transformedMessages =
      conversation?.messages?.map(msg => ({
        id: crypto.randomUUID(),
        role: msg.role === 'user' ? MessageRole.USER : MessageRole.ASSISTANT,
        content: typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content),
      })) || [];

    logger.debug('🗂️ Store: transformed messages count:', transformedMessages.length);

    set({
      activeConversation: conversation,
      messages: transformedMessages,
      lastActiveConversationId: conversation?.id ?? get().lastActiveConversationId,
    });
  },

  patchActiveConversation: patch =>
    set(state => ({
      activeConversation: state.activeConversation
        ? { ...state.activeConversation, ...patch }
        : null,
    })),

  setMessages: messages => set({ messages }),

  addMessage: message =>
    set(state => ({
      messages: [...state.messages, message],
    })),

  insertMessageBefore: (message, beforeId) =>
    set(state => {
      const index = state.messages.findIndex(msg => msg.id === beforeId);
      if (index === -1) {
        // If target not found, append to end
        return { messages: [...state.messages, message] };
      }
      // Insert before the target message
      const newMessages = [
        ...state.messages.slice(0, index),
        message,
        ...state.messages.slice(index),
      ];
      return { messages: newMessages };
    }),

  updateMessage: (id, updates) =>
    set(state => {
      logger.debug('🔄 Store: updateMessage called', {
        id,
        updates,
        messageCount: state.messages.length,
      });
      const updatedMessages = state.messages.map(msg =>
        msg.id === id ? { ...msg, ...updates } : msg
      );
      logger.debug('🔄 Store: updateMessage result', {
        found: updatedMessages.some(msg => msg.id === id),
        updatedMessage: updatedMessages.find(msg => msg.id === id),
      });
      return { messages: updatedMessages };
    }),

  appendToMessage: (id, content) =>
    set(state => ({
      messages: state.messages.map(msg =>
        msg.id === id ? { ...msg, content: (msg.content || '') + content } : msg
      ),
    })),

  appendHtmlEvent: (id, event) =>
    set(state => ({
      messages: state.messages.map(msg =>
        // New array each push so consumers re-render (htmlEvents is transient).
        msg.id === id ? { ...msg, htmlEvents: [...(msg.htmlEvents ?? []), event] } : msg
      ),
    })),

  updateActivity: (id, fn) =>
    set(state => {
      const messages = applyActivityUpdate(state.messages, id, fn);
      return messages === state.messages ? state : { messages };
    }),

  removeMessage: id =>
    set(state => ({
      messages: state.messages.filter(msg => msg.id !== id),
    })),

  clearMessages: () => {
    logger.info('🧹 clearMessages called - resetting to empty messages array');
    set({
      messages: [],
      activeConversation: null,
      isStreaming: false,
    });
  },

  setIsStreaming: streaming => set({ isStreaming: streaming }),

  setIsLoadingConversation: loading => set({ isLoadingConversation: loading }),

  setPendingMessage: pending => set({ pendingMessage: pending }),

  addContextOverflow: conversationId =>
    set(state => {
      const next = new Set(state.contextOverflowConversationIds);
      next.add(conversationId);
      return { contextOverflowConversationIds: next };
    }),

  clearContextOverflow: conversationId =>
    set(state => {
      if (!state.contextOverflowConversationIds.has(conversationId)) return {};
      const next = new Set(state.contextOverflowConversationIds);
      next.delete(conversationId);
      return { contextOverflowConversationIds: next };
    }),

  setCompacting: conversationId => set({ compactingConversationId: conversationId }),
});
